import { Component, OnInit, OnChanges, Input, EventEmitter, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

import { BaseComponent } from 'app/_components/base.component';

import { AuthenticationService, CompanyService } from 'app/_services';

import { DATE_TIME_SUB_TYPE, MULTIPLE_RADIO_EVENT_DETAIL_SUB_TYPE, NUMBER_SUB_TYPE } from 'app/app.constants';

@Component({
  selector: 'app-request-question-row-edit',
  templateUrl: './request-question-row-edit.component.html',
  styleUrls: ['./request-question-row-edit.component.scss']
})
export class RequestQuestionRowEditComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() priority: string;
  @Input() questions: any[];
  @Input() depth: number;
  @Input() question: any;
  @Input() questionTypes: any[];
  @Input() dedicatedQuestionTypes: any[];
  @Input() nextScreens: any[];
  @Input() meetingTypes: any[] = [];
  @Input() alignedMeetingTypes: any[] = [];
  @Input() programSeriesOptions: any[] = [];
  @Input() formatOptions: any[] = [];
  @Input() speakerCountOptions: any[] = [];
  @Output() delete: EventEmitter<any> = new EventEmitter();
  newQuestion: any;

  DATE_TIME_SUB_TYPE = DATE_TIME_SUB_TYPE;
  MULTIPLE_RADIO_EVENT_DETAIL_SUB_TYPE = MULTIPLE_RADIO_EVENT_DETAIL_SUB_TYPE;
  NUMBER_SUB_TYPE = NUMBER_SUB_TYPE;

  constructor(
    router: Router,
    authenticationService: AuthenticationService,
    companyService: CompanyService,
  ) {
    super(router, authenticationService, companyService);
  }

  ngOnInit() {
    this.newQuestion = {
      type_id: null,
      sub_type: null,
      text: '',
      packages: null,
      equipments: null,
      min: 1,
      max: 1,
      skipScreen: -1,
      subQuestions: []
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.question && (changes.alignedMeetingTypes || changes.question) && this.isNumberMinFromMeetingTypes(this.question)) {
      this.question.min = this.getNumberMinFromAlignedMeetingTypes(this.question);
    }
  }

  getScreen() {
    return this.question.skipScreen == 'END' ? { name: 'END' } : this.nextScreens.filter(item => item.index == this.question.skipScreen)[0];
  }

  onDeleteQ(index) {
    this.question.subQuestions.splice(index, 1);
  }

  needOptionalCheckbox(question) {
    return question.type_id === 'AMOUNT' || question.type_id === 'CHECKBOX'
      || question.type_id === 'TEXT_FIELD' || question.type_id === 'PHONE_NUMBER'
      || question.type_id === 'EMAIL'
      || question.type_id === 'TEXT_AREA' || question.type_id === 'state'
      || question.type_id === 'NUMBER' || question.type_id === 'FILE'
      || question.type_id === 'meal_type';
  }

  /** Whether this MULTIPLE_RADIO uses event-detail sub type (choices from dropdown). */
  hasRadioEventDetailSubType(question: any): boolean {
    return question.type_id === 'MULTIPLE_RADIO' && question.sub_type;
  }

  /** Full list of options for the selected sub type (for pre-fill). */
  getRadioEventDetailFullOptions(question: any): { value: string; label: string }[] {
    if (!question || question.type_id !== 'MULTIPLE_RADIO' || !question.sub_type) return [];
    switch (question.sub_type) {
      case 'event_type':
        return (this.meetingTypes || []).map((m: any) => ({ value: String(m.id), label: m.name || String(m.id) }));
      case 'program_series':
        return (this.programSeriesOptions || []).map((s: any) => ({
          value: s.value != null ? String(s.value) : (s.id != null ? String(s.id) : ''),
          label: s.label || s.name || String(s.value != null ? s.value : s.id || '')
        }));
      case 'attendee_format':
      case 'speaker_format':
        return (this.formatOptions || []).map((f: any) => ({ value: f.value, label: f.label || f.value }));
      case 'speaker_count':
        return (this.speakerCountOptions || []).map((c: any) => ({
          value: String(c.value),
          label: c.label || String(c.value)
        }));
      default:
        return [];
    }
  }

  /** Pre-fill choices from event-detail dropdown when sub type is selected. */
  onRadioSubTypeChange(question: any) {
    if (!question || question.type_id !== 'MULTIPLE_RADIO') return;
    const opts = this.getRadioEventDetailFullOptions(question);
    question.choices = opts.map(o => (o.value === o.label ? o.value : `${o.value}|${o.label}`)).join('\n');
  }

  /** Parse question.choices into array of { value, label } (one per line, optional "value|label"). */
  getRadioChoiceList(question: any): { value: string; label: string }[] {
    if (!question || !question.choices || typeof question.choices !== 'string') return [];
    return question.choices.split('\n').filter((line: string) => line.trim()).map((line: string) => {
      const t = line.trim();
      const i = t.indexOf('|');
      if (i >= 0) return { value: t.substring(0, i).trim(), label: t.substring(i + 1).trim() || t };
      return { value: t, label: t };
    });
  }

  /** Remove choice at index; at least one option must remain. */
  removeRadioChoice(question: any, index: number) {
    const list = this.getRadioChoiceList(question);
    if (list.length <= 1) return;
    list.splice(index, 1);
    question.choices = list.map(o => (o.value === o.label ? o.value : `${o.value}|${o.label}`)).join('\n');
  }

  /** For NUMBER + Expected Audience: max Min Attendees among aligned meeting types. For Expected HCPs: max Min HCP. */
  getNumberMinFromAlignedMeetingTypes(question: any): number {
    if (!question || question.type_id !== 'NUMBER' || !this.alignedMeetingTypes || !this.alignedMeetingTypes.length) return 0;
    if (question.sub_type === 'expected_audience') {
      const values = this.alignedMeetingTypes.map((m: any) => m.min_attendees).filter((n: any) => n != null && n !== '');
      return values.length ? Math.max(...values.map((n: any) => Number(n))) : 0;
    }
    if (question.sub_type === 'expected_hcps') {
      const values = this.alignedMeetingTypes.map((m: any) => m.min_hcp).filter((n: any) => n != null && n !== '');
      return values.length ? Math.max(...values.map((n: any) => Number(n))) : 0;
    }
    return 0;
  }

  /** Whether NUMBER question min is driven by aligned meeting types (read-only). */
  isNumberMinFromMeetingTypes(question: any): boolean {
    return question && question.type_id === 'NUMBER' && (question.sub_type === 'expected_audience' || question.sub_type === 'expected_hcps');
  }

  /** When NUMBER sub_type changes to Expected Audience or Expected HCPs, set question.min from aligned meeting types. */
  onNumberSubTypeChange(question: any) {
    if (this.isNumberMinFromMeetingTypes(question)) {
      question.min = this.getNumberMinFromAlignedMeetingTypes(question);
    }
  }

  Delete() {
    this.delete.emit();
  }

  drop(q: CdkDragDrop<any[]>) {
    const question = q.item.data;
    if (q.container.id === q.previousContainer.id) {
      this.question.subQuestions[q.previousIndex] = this.question.subQuestions[q.currentIndex];
      this.question.subQuestions[q.previousIndex].rank = q.previousIndex + 1
      this.question.subQuestions[q.currentIndex] = question;
      this.question.subQuestions[q.currentIndex].rank = q.currentIndex + 1;
    }
  }
}
